import { useTranslation } from "react-i18next";
import type { PdfFileRow } from "../../db/pdfRepository";
import InlineFilenameEditor from "./InlineFilenameEditor";
import AutoPlayToggle from "./AutoPlayToggle";
import PdfCardThumbnail from "./PdfCardThumbnail";
import PdfCardActionsBar, { type PdfCardActions } from "./PdfCardActions";

export type PdfCardEditState = {
  isEditing: boolean;
  value: string;
  onStart: () => void;
  onChange: (value: string) => void;
  onSave: () => void;
  onCancel: () => void;
};

type PdfCardProps = {
  pdf: PdfFileRow;
  timeAgoLabel: string;
  formattedFileSize: string;
  edit: PdfCardEditState;
  onAutoPlayChange: (checked: boolean) => void;
  onPlay: () => void;
  actions: PdfCardActions;
};

export default function PdfCard({
  pdf,
  timeAgoLabel,
  formattedFileSize,
  edit,
  onAutoPlayChange,
  onPlay,
  actions,
}: PdfCardProps) {
  const { t } = useTranslation();
  const isConverting = pdf.status === "converting";
  const fileName = pdf.file_name?.replace(".pdf", "") || "Qpresen 소개자료";

  return (
    <article className="w-full overflow-hidden rounded-xl border border-border bg-card text-card-foreground shadow-sm">
      <PdfCardThumbnail
        thumbnailUrl={pdf.thumbnail_url}
        fileName={pdf.file_name}
        isConverting={isConverting}
        onPlay={onPlay}
      />

      <div className="p-4">
        {edit.isEditing ? (
          <InlineFilenameEditor
            value={edit.value}
            onChange={edit.onChange}
            onSave={edit.onSave}
            onCancel={edit.onCancel}
          />
        ) : (
          <div>
            <div className="mb-0.5 flex items-center gap-2">
              <h3 className="flex-1 truncate text-sm font-semibold text-foreground sm:text-base">
                {fileName}
              </h3>
              <button
                type="button"
                onClick={edit.onStart}
                aria-label={t("common.save")}
                className="flex h-6 w-6 flex-shrink-0 cursor-pointer items-center justify-center rounded text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <i className="ri-edit-line text-sm" aria-hidden="true" />
              </button>
            </div>
            <div className="mb-2 flex items-center gap-1 text-xs text-muted-foreground">
              <i className="ri-time-line" aria-hidden="true" />
              {timeAgoLabel}
            </div>
          </div>
        )}

        <p className="mb-3 flex items-center gap-2 text-xs text-muted-foreground sm:gap-3 sm:text-sm">
          <span className="flex items-center gap-1">
            <i className="ri-file-list-3-line" aria-hidden="true" />
            {pdf.page_count}P
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-hard-drive-2-line" aria-hidden="true" />
            {formattedFileSize}
          </span>
        </p>

        <AutoPlayToggle
          checked={pdf.auto_fullscreen || false}
          onChange={onAutoPlayChange}
          label={t("dashboard.auto_play_on_login")}
        />

        <PdfCardActionsBar {...actions} disabled={isConverting} />
      </div>
    </article>
  );
}
